import { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Tiny recharts wrapper used by `/comparison` and the readiness "over time"
 * tab. Plots readiness score (0–100) and credit score (300–850) on dual axes.
 */
export default function ScoreHistoryChart({ points, height = 280, compact = false }) {
  const data = useMemo(() => {
    return (points || []).map((p) => ({
      ts: new Date(p.created_at).getTime(),
      readiness: p.readiness_score ?? null,
      credit: p.credit_score ?? null,
      label: new Date(p.created_at).toLocaleDateString(),
    }));
  }, [points]);

  if (!data.length) return null;
  const hasCredit = data.some((d) => d.credit != null);
  return (
    <div
      data-testid="score-history-chart"
      className="rounded-2xl p-5"
      style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
    >
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 12, right: 18, left: 0, bottom: 12 }}>
          <CartesianGrid stroke="var(--ds-color-border)" strokeDasharray="2 4" />
          <XAxis
            dataKey="label"
            stroke="var(--ds-color-ink-subtle)"
            fontSize={11}
            tickMargin={6}
          />
          <YAxis
            yAxisId="readiness"
            domain={[0, 100]}
            stroke="var(--ds-color-accent-strong)"
            fontSize={11}
            width={36}
            tickMargin={4}
          />
          {hasCredit && (
            <YAxis
              yAxisId="credit"
              orientation="right"
              domain={[300, 850]}
              stroke="var(--ds-color-success-muted)"
              fontSize={11}
              width={36}
              tickMargin={4}
            />
          )}
          <Tooltip
            contentStyle={{
              background: "var(--ds-color-surface)",
              border: "1px solid var(--ds-color-border)",
              borderRadius: "var(--ds-radius-input)",
              color: "var(--ds-color-ink)",
              fontSize: 12,
            }}
            labelStyle={{ color: "var(--ds-color-ink-muted)" }}
          />
          {!compact && (
            <Legend
              wrapperStyle={{
                fontSize: 11,
                color: "var(--ds-color-ink-muted)",
                paddingTop: 8,
              }}
            />
          )}
          <Line
            yAxisId="readiness"
            type="monotone"
            dataKey="readiness"
            name="Readiness"
            stroke="var(--ds-color-accent-strong)"
            strokeWidth={2}
            dot={{ r: 3, fill: "var(--ds-color-accent-strong)" }}
            connectNulls
            isAnimationActive={false}
          />
          {hasCredit && (
            <Line
              yAxisId="credit"
              type="monotone"
              dataKey="credit"
              name="Credit score"
              stroke="var(--ds-color-success-muted)"
              strokeWidth={2}
              dot={{ r: 3, fill: "var(--ds-color-success-muted)" }}
              connectNulls
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
